import React, { useMemo } from 'react';
import { detectSectionType } from '@/utils/report/sectionUtils';
import { markdownHeadingPattern, numberedSectionPattern } from '@/utils/report/sectionUtils';

// Import subcomponents
import SwotSection from './sections/SwotSection';
import IntroductionSection from './sections/IntroductionSection';
import MajorSectionHeading from './sections/MajorSectionHeading';
import BulletListSection from './sections/BulletListSection';
import NumberedListSection from './sections/NumberedListSection';
import TableSection from './sections/TableSection';
import SubsectionHeading from './sections/SubsectionHeading';
import ParagraphSection from './sections/ParagraphSection';

// Import lazy-loaded components for optimization
const LazyReportTable = React.lazy(() => import('./ui/ReportTable'));

type UnifiedReportContentProps = {
  sections: string[];
  majorSections: string[];
};

/**
 * A unified component that renders all types of report sections with consistent formatting.
 * This combines functionality from both ReportMainContent and ReportMainSection.
 * Refactored for improved performance and maintainability.
 */
const UnifiedReportContent = ({ sections, majorSections }: UnifiedReportContentProps) => {
  // Return early if no sections
  if (!sections || sections.length === 0) {
    return null;
  }

  // Process sections in a useMemo to avoid unnecessary recalculation
  const processedSections = useMemo(() => {
    return sections.map((section, index) => {
      // Skip empty sections
      if (!section.trim()) return null;
      
      // Skip references sections - they will be entirely excluded from the chat UI
      if (section.toLowerCase().includes('references') || 
          section.toLowerCase().includes('## references') || 
          section.toLowerCase().includes('# references')) {
        return null;
      }
      
      // Skip separator lines that only contain dashes
      if (/^-+$/.test(section.trim()) || /^--+$/.test(section.trim())) {
        return null;
      }
      
      // Remove markdown heading symbols from section text to prevent duplication in display
      let cleanedSection = section;
      const headingMatch = section.match(markdownHeadingPattern);
      
      if (headingMatch) {
        // If this is a markdown heading, extract just the heading text
        cleanedSection = headingMatch[2].trim();
      } else {
        // Otherwise clean up the section text normally
        cleanedSection = section.replace(/^#+\s+/g, '').trim();
      }
      
      // Remove all # characters from section text for comparison
      const comparisonText = cleanedSection.replace(/#/g, '').trim();
      
      // Check if this is a numbered section heading (like "4. Financial Performance")
      const numberedSectionMatch = cleanedSection.match(numberedSectionPattern);
      const isMajorSection = majorSections.some(majorSection => {
        const majorPattern = new RegExp(`\\b${majorSection.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        return majorPattern.test(comparisonText);
      });
      
      // Determine section type
      const sectionType = detectSectionType(cleanedSection);
      
      // Return the appropriate component based on section type
      switch (sectionType) {
        case 'references':
          return null; // Skip references as they're excluded from Chat UI
          
        case 'swot':
          return <SwotSection content={section} index={index} />;
          
        case 'introduction':
          return <IntroductionSection content={cleanedSection} index={index} />;
          
        case 'majorSection':
        case null:
          // Handle major section headings separately
          if (isMajorSection || numberedSectionMatch) {
            return <MajorSectionHeading content={cleanedSection} index={index} />;
          }
          // Fall through to default if not a major section
          
        case 'bulletList':
          return <BulletListSection content={cleanedSection} index={index} />;
          
        case 'numberedList':
          return <NumberedListSection content={cleanedSection} index={index} />;
          
        case 'table':
          return (
            <React.Suspense fallback={<div>Loading table...</div>}>
              <TableSection content={cleanedSection} index={index} />
            </React.Suspense>
          );
          
        case 'heading':
          return <SubsectionHeading content={cleanedSection} index={index} />;
          
        case 'paragraph':
        default:
          return <ParagraphSection content={cleanedSection} index={index} sections={sections} />;
      }
    }).filter(Boolean); // Filter out null values
  }, [sections, majorSections]);

  return (
    <div className="space-y-6 mt-6">
      {processedSections}
    </div>
  );
};

export default UnifiedReportContent;
